import { useState, useEffect, useRef, useCallback } from "react";
import { MapPin, Loader2, X } from "lucide-react";
import { Input } from "@/components/ui/input";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

const GooglePlacesAutocomplete = ({ 
  value, 
  onChange, 
  placeholder = "Enter your address",
  className = "",
  disabled = false 
}) => {
  const [inputValue, setInputValue] = useState(value || "");
  const [predictions, setPredictions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const debounceTimer = useRef(null);

  // Load Google Maps script
  useEffect(() => {
    if (window.google?.maps?.places) {
      setIsScriptLoaded(true);
      return;
    }

    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      existingScript.addEventListener('load', () => setIsScriptLoaded(true));
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => setIsScriptLoaded(true);
    script.onerror = () => console.error('Failed to load Google Maps script');
    document.head.appendChild(script);

    return () => {
      // Don't remove the script on unmount as other components might use it
    };
  }, []);

  // Initialize services when script is loaded
  useEffect(() => {
    if (isScriptLoaded && window.google?.maps?.places) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
      // Create a dummy div for PlacesService (required by the API)
      const dummyElement = document.createElement('div');
      placesService.current = new window.google.maps.places.PlacesService(dummyElement);
    }
  }, [isScriptLoaded]);

  // Sync external value changes
  useEffect(() => {
    if (value !== inputValue) {
      setInputValue(value || "");
    }
  }, [value]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchPredictions = useCallback((input) => {
    if (!autocompleteService.current || !input.trim()) {
      setPredictions([]);
      return;
    }

    setIsLoading(true);

    autocompleteService.current.getPlacePredictions(
      {
        input,
        types: ['address'],
        componentRestrictions: { country: 'jp' }, // Restrict to Japan (Okinawa)
      },
      (results, status) => {
        setIsLoading(false);
        if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
          setPredictions(results);
          setShowDropdown(true);
        } else {
          setPredictions([]);
        }
      }
    );
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Debounce the API call
    debounceTimer.current = setTimeout(() => {
      if (newValue.length >= 3) {
        fetchPredictions(newValue);
      } else {
        setPredictions([]);
        setShowDropdown(false);
      }
    }, 300);
  };

  const handleSelectPrediction = (prediction) => {
    // Get place details to get the full formatted address
    if (placesService.current) {
      placesService.current.getDetails(
        { placeId: prediction.place_id, fields: ['formatted_address', 'geometry'] },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
            const fullAddress = place.formatted_address || prediction.description;
            setInputValue(fullAddress);
            onChange(fullAddress);
            setShowDropdown(false);
            setPredictions([]);
          } else {
            // Fallback to prediction description
            setInputValue(prediction.description);
            onChange(prediction.description);
            setShowDropdown(false);
            setPredictions([]);
          }
        }
      );
    } else {
      setInputValue(prediction.description);
      onChange(prediction.description);
      setShowDropdown(false);
      setPredictions([]);
    }
  };

  const handleClear = () => {
    setInputValue("");
    onChange("");
    setPredictions([]);
    setShowDropdown(false);
    inputRef.current?.focus();
  };

  return (
    <div className="relative">
      <div className="relative">
        <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA] z-10" />
        <Input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={() => predictions.length > 0 && setShowDropdown(true)}
          placeholder={placeholder}
          disabled={disabled}
          className={`pl-10 pr-10 bg-[#121212] border-[#27272A] text-white focus:border-[#39FF14] ${className}`}
          autoComplete="off"
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#39FF14] animate-spin" />
        )}
        {!isLoading && inputValue && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Predictions Dropdown */}
      {showDropdown && predictions.length > 0 && (
        <div 
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-[#1a1a1a] border border-[#27272A] rounded-xl shadow-xl overflow-hidden"
        >
          {predictions.map((prediction) => (
            <button
              key={prediction.place_id}
              type="button"
              onClick={() => handleSelectPrediction(prediction)}
              className="w-full px-4 py-3 text-left hover:bg-[#27272A] transition-colors flex items-start gap-3 border-b border-[#27272A] last:border-b-0"
            >
              <MapPin className="w-4 h-4 text-[#39FF14] mt-0.5 shrink-0" />
              <div>
                <p className="text-white text-sm">{prediction.structured_formatting?.main_text}</p>
                <p className="text-[#A1A1AA] text-xs">{prediction.structured_formatting?.secondary_text}</p>
              </div>
            </button>
          ))}
          <div className="px-4 py-2 bg-[#121212] flex items-center justify-end gap-1">
            <span className="text-[#A1A1AA] text-xs">Powered by</span>
            <img 
              src="https://maps.gstatic.com/mapfiles/api-3/images/powered-by-google-on-white3_hdpi.png" 
              alt="Google" 
              className="h-3 opacity-70"
              style={{ filter: 'invert(1)' }}
            />
          </div>
        </div>
      )}

      {/* No results message */}
      {showDropdown && inputValue.length >= 3 && !isLoading && predictions.length === 0 && (
        <div className="absolute z-50 w-full mt-1 bg-[#1a1a1a] border border-[#27272A] rounded-xl shadow-xl p-4 text-center">
          <p className="text-[#A1A1AA] text-sm">No addresses found. Try a different search.</p>
        </div>
      )}
    </div>
  );
};

export default GooglePlacesAutocomplete;
